import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  Product,
  ProductionWorkOrder,
  ProductionWorkOrderBatch,
  ProductionWorkOrderPayload,
  ProductionWorkOrderStatus,
  UnitType,
  Warehouse,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import SearchableSelect from "../../../components/DataEntry/SearchableSelect";
import { batchStatusLabel, formatHrDate, formatNum } from "../Batches/batchUtils";
import BatchProcessDataEditor from "./BatchProcessDataEditor";

/**
 * Nalozi proizvodnje — pregled, uređivanje i procesni podaci.
 *
 * Podloga je dbo."ProductionWorkOrder" (platformska tablica bez Prisma modela,
 * pa backend piše raw SQL-om). Nalog nema kolonu prema zamjesu, zato se zamjesi
 * koji ga izvršavaju izvode: isti proizvod, proizveden u rasponu datuma oko roka
 * naloga. Raspon je vidljiv i podesiv jer se plan u praksi pomiče, a operater
 * mora vidjeti i te zamjese.
 *
 * Procesni podaci zamjesa uređuju se ovdje istim setom polja kao na tabletu i na
 * ekranu "Procesni podaci" (processFields.ts) — to je jedan i isti red u
 * zboogie_process_parameters.
 */

const isoDate = (d: Date) => d.toISOString().split("T")[0];
const shiftDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + days);
  return isoDate(d);
};

/** Isti prozor koji backend koristi kad raspon nije poslan. */
const DEFAULT_WINDOW_DAYS = 7;

type FormState = {
  WorkOrderNumber: string;
  Product: number;
  QuantityToProduce: string;
  UnitType: number;
  Status: number;
  Priority: string;
  DueDate: string;
  Warehouse: number;
  Notes: string;
};

const emptyForm: FormState = {
  WorkOrderNumber: "",
  Product: 0,
  QuantityToProduce: "",
  UnitType: 0,
  Status: 0,
  Priority: "",
  DueDate: "",
  Warehouse: 0,
  Notes: "",
};

const formFromOrder = (o: ProductionWorkOrder): FormState => ({
  WorkOrderNumber: o.WorkOrderNumber || "",
  Product: o.Product ?? 0,
  QuantityToProduce: o.QuantityToProduce != null ? String(o.QuantityToProduce) : "",
  UnitType: o.UnitType ?? 0,
  Status: o.Status ?? 0,
  Priority: o.Priority != null ? String(o.Priority) : "",
  DueDate: o.DueDate ? String(o.DueDate).slice(0, 10) : "",
  Warehouse: o.Warehouse ?? 0,
  Notes: o.Notes || "",
});

/** Prazan unos znači "bez vrijednosti" (NULL), ne 0. */
const numOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/**
 * `forCreate` prazan broj naloga šalje kao null da ga backend dodijeli
 * (RN-godina-broj). Pri ažuriranju se prazno polje NE šalje: slučajno brisanje
 * inače nalogu tiho ukine oznaku po kojoj ga proizvodnja zove.
 */
const payloadFromForm = (form: FormState, forCreate: boolean): ProductionWorkOrderPayload => ({
  ...(forCreate || form.WorkOrderNumber.trim()
    ? { WorkOrderNumber: form.WorkOrderNumber.trim() || null }
    : {}),
  Product: form.Product || null,
  QuantityToProduce: numOrNull(form.QuantityToProduce),
  UnitType: form.UnitType || null,
  Status: form.Status || null,
  Priority: numOrNull(form.Priority),
  DueDate: form.DueDate || null,
  Warehouse: form.Warehouse || null,
  Notes: form.Notes.trim() || null,
});

const ProductionWorkOrders = () => {
  savePathToLocalStorage();

  const [orders, setOrders] = useState<ProductionWorkOrder[]>([]);
  const [statuses, setStatuses] = useState<ProductionWorkOrderStatus[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  // --- detalj / uređivanje ---
  const [selected, setSelected] = useState<ProductionWorkOrder | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [modalTab, setModalTab] = useState<"order" | "batches">("order");

  // --- vezani zamjesi ---
  const [batches, setBatches] = useState<ProductionWorkOrderBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState<number | null>(null);
  const [range, setRange] = useState<{ fromDate: string; toDate: string }>({ fromDate: "", toDate: "" });

  useEffect(() => {
    // Lookupi za uređivanje naloga; padaju tiho jer je pregled koristan i bez njih.
    Promise.all([
      mesApi.getProductionWorkOrderStatuses().then(setStatuses),
      mesApi.getProducts({ is_active: true }).then(setProducts),
      mesApi.getUnitTypes().then(setUnitTypes),
      mesApi.getSkladista().then(setWarehouses),
    ]).catch((e) => console.error("Failed to load PWO lookups", e));
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mesApi.getProductionWorkOrders();
      setOrders(data || []);
      setLoaded(true);
    } catch (err: any) {
      toast.error("Greška pri učitavanju naloga proizvodnje: " + err.message);
      setOrders([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter && String(o.Status) !== statusFilter) return false;
      if (!q) return true;
      return (
        (o.WorkOrderNumber || "").toLowerCase().includes(q) ||
        (o.ProductName || "").toLowerCase().includes(q) ||
        (o.ProductCode || "").toLowerCase().includes(q)
      );
    });
  }, [orders, statusFilter, search]);

  const totalQty = useMemo(
    () => filtered.reduce((sum, o) => sum + (Number(o.QuantityToProduce) || 0), 0),
    [filtered]
  );

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.ID,
        label: p.ProductName,
        sublabel: p.ProductCode || undefined,
      })),
    [products]
  );

  const statusPill = (o: ProductionWorkOrder) => {
    const label = o.StatusName || o.StatusCode || "-";
    const style: React.CSSProperties = {
      backgroundColor: o.StatusBackgroundColor || "#eee",
      borderColor: o.StatusBorderColor || "#ccc",
      color: o.StatusTextColor || "#333",
      border: "1px solid",
      borderRadius: "12px",
      padding: "2px 10px",
      fontSize: "0.8rem",
      whiteSpace: "nowrap",
    };
    return <span style={style}>{label}</span>;
  };

  const openNew = () => {
    setSelected(null);
    setIsNew(true);
    setForm({ ...emptyForm, DueDate: isoDate(new Date()) });
    setBatches([]);
    setModalTab("order");
  };

  const openOrder = (o: ProductionWorkOrder) => {
    setSelected(o);
    setIsNew(false);
    setForm(formFromOrder(o));
    setModalTab("order");
    setExpandedBatch(null);

    const due = o.DueDate ? String(o.DueDate).slice(0, 10) : isoDate(new Date());
    setRange({
      fromDate: shiftDays(due, -DEFAULT_WINDOW_DAYS),
      toDate: shiftDays(due, DEFAULT_WINDOW_DAYS),
    });
    setBatches([]);
  };

  const closeModal = () => {
    setSelected(null);
    setIsNew(false);
    setBatches([]);
    setExpandedBatch(null);
  };

  const loadBatches = useCallback(
    async (orderId: number, r: { fromDate: string; toDate: string }) => {
      setBatchesLoading(true);
      try {
        const data = await mesApi.getProductionWorkOrderBatches(orderId, r);
        setBatches(data || []);
      } catch (err: any) {
        toast.error("Greška pri učitavanju zamjesa: " + err.message);
        setBatches([]);
      } finally {
        setBatchesLoading(false);
      }
    },
    []
  );

  // Zamjesi se dohvaćaju kad se otvori njihov tab (i na promjenu raspona), ne
  // pri otvaranju naloga — većina otvaranja je zbog uređivanja zaglavlja.
  useEffect(() => {
    if (modalTab !== "batches" || !selected) return;
    loadBatches(selected.ID, range);
  }, [modalTab, selected, range, loadBatches]);

  const saveOrder = async () => {
    if (!form.Product) {
      toast.error("Odaberite proizvod");
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form, isNew);
      if (isNew) {
        const created = await mesApi.createProductionWorkOrder(payload);
        toast.success(`Nalog ${created.WorkOrderNumber || `#${created.ID}`} kreiran`);
        setSelected(created);
        setIsNew(false);
        setForm(formFromOrder(created));
      } else if (selected) {
        const updated = await mesApi.updateProductionWorkOrder(selected.ID, payload);
        toast.success("Nalog ažuriran");
        setSelected(updated);
        setForm(formFromOrder(updated));
      }
      await loadOrders();
    } catch (err: any) {
      toast.error("Greška pri spremanju: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const modalOpen = isNew || selected != null;

  return (
    <div className="mes-page">
      <Header title="MES - Nalozi proizvodnje" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2000} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "16px 0",
            }}
          >
            <h2>Nalozi proizvodnje</h2>
            <span style={{ color: "#999", fontSize: "0.9rem" }}>
              {filtered.length} naloga · {formatNum(totalQty, 0)} kom (ukupno)
            </span>
          </div>

          <div
            className="mes-section"
            style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "16px" }}
          >
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: "220px" }}>
              <label>Pretraga (broj naloga / proizvod)</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="npr. RN-2026…, naziv proizvoda…"
              />
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: "160px" }}>
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Svi</option>
                {statuses.map((s) => (
                  <option key={s.ID} value={String(s.ID)}>
                    {s.Name || s.Code}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn-secondary" onClick={loadOrders} disabled={loading}>
              {loading ? "Učitavanje…" : "Osvježi"}
            </button>
            <button className="btn-submit" onClick={openNew}>
              + Novi nalog
            </button>
          </div>

          <div className="mes-section">
            {loading ? (
              <p style={{ textAlign: "center", padding: "20px" }}>Učitavanje…</p>
            ) : filtered.length === 0 ? (
              <p style={{ textAlign: "center", padding: "40px", color: "#999", fontStyle: "italic" }}>
                {loaded ? "Nema naloga proizvodnje." : "Učitavanje…"}
              </p>
            ) : (
              <div className="mes-table-wrap">
                <table className="mes-table">
                  <thead>
                    <tr>
                      <th>Broj naloga</th>
                      <th>Proizvod</th>
                      <th style={{ textAlign: "right" }}>Količina</th>
                      <th>Jedinica</th>
                      <th>Prioritet</th>
                      <th>Rok (Due date)</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((o) => (
                      <tr key={o.ID}>
                        <td>
                          <strong>{o.WorkOrderNumber || `#${o.ID}`}</strong>
                        </td>
                        <td>
                          {o.ProductName || "-"}
                          {o.ProductCode ? (
                            <span style={{ marginLeft: 6, color: "#999", fontSize: "0.8rem" }}>
                              ({o.ProductCode})
                            </span>
                          ) : null}
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                          {formatNum(o.QuantityToProduce, 0)}
                        </td>
                        <td>{o.UnitTypeName || o.UnitTypeCode || "-"}</td>
                        <td>{o.Priority != null ? o.Priority : "-"}</td>
                        <td>{formatHrDate(o.DueDate)}</td>
                        <td>{statusPill(o)}</td>
                        <td>
                          <button className="btn-edit btn-sm" onClick={() => openOrder(o)}>
                            Uredi
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="mes-modal-overlay" onClick={closeModal}>
          <div className="mes-modal mes-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">🧾</div>
                <div className="modal-title-text">
                  <h3>{isNew ? "NOVI NALOG PROIZVODNJE" : "NALOG PROIZVODNJE"}</h3>
                  <p className="modal-subtitle">
                    {isNew
                      ? "Broj se dodjeljuje automatski (RN-godina-broj) ako ga ne upišete"
                      : `${selected?.WorkOrderNumber || `#${selected?.ID}`} · ${selected?.ProductName || "—"}`}
                  </p>
                </div>
              </div>
              <button className="close-btn" onClick={closeModal}>
                ✕
              </button>
            </div>

            {!isNew && (
              <div className="modal-tabs" style={{ display: "flex", borderBottom: "1px solid #ddd", marginBottom: "1rem" }}>
                {([
                  { id: "order", label: "Nalog" },
                  { id: "batches", label: "Zamjesi i procesni podaci" },
                ] as const).map((t) => (
                  <button
                    key={t.id}
                    className={`modal-tab ${modalTab === t.id ? "active" : ""}`}
                    onClick={() => setModalTab(t.id)}
                    style={{
                      padding: "0.75rem 1.5rem",
                      border: "none",
                      background: modalTab === t.id ? "#4b9987" : "transparent",
                      color: modalTab === t.id ? "#fff" : "#666",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {isNew || modalTab === "order" ? (
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Broj naloga</label>
                    <input
                      type="text"
                      value={form.WorkOrderNumber}
                      placeholder={isNew ? "automatski" : ""}
                      onChange={(e) => setForm({ ...form, WorkOrderNumber: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Proizvod</label>
                    <SearchableSelect
                      value={form.Product}
                      options={productOptions}
                      onChange={(v) => setForm({ ...form, Product: v })}
                      placeholder="Pretraži proizvod…"
                    />
                  </div>
                  <div className="form-group">
                    <label>Količina za proizvodnju</label>
                    <input
                      type="number"
                      value={form.QuantityToProduce}
                      onChange={(e) => setForm({ ...form, QuantityToProduce: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Jedinica</label>
                    <select
                      value={String(form.UnitType)}
                      onChange={(e) => setForm({ ...form, UnitType: Number(e.target.value) })}
                    >
                      <option value="0">—</option>
                      {unitTypes.map((u) => (
                        <option key={u.ID} value={u.ID}>
                          {u.Name || u.Code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={String(form.Status)}
                      onChange={(e) => setForm({ ...form, Status: Number(e.target.value) })}
                    >
                      <option value="0">—</option>
                      {statuses.map((s) => (
                        <option key={s.ID} value={s.ID}>
                          {s.Name || s.Code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Prioritet</label>
                    <input
                      type="number"
                      value={form.Priority}
                      onChange={(e) => setForm({ ...form, Priority: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Rok (Due date)</label>
                    <input
                      type="date"
                      value={form.DueDate}
                      onChange={(e) => setForm({ ...form, DueDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Skladište</label>
                    <select
                      value={String(form.Warehouse)}
                      onChange={(e) => setForm({ ...form, Warehouse: Number(e.target.value) })}
                    >
                      <option value="0">—</option>
                      {warehouses.map((w) => (
                        <option key={w.warehouse_id} value={w.warehouse_id}>
                          {w.name || w.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label>Napomena</label>
                    <textarea
                      value={form.Notes}
                      onChange={(e) => setForm({ ...form, Notes: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <p className="mes-hint" style={{ marginTop: 0 }}>
                    Nalog proizvodnje nema vezu na pojedinu zamjesu, pa se prikazuju zamjesi istog
                    proizvoda proizvedeni u ovom rasponu datuma. Proširite raspon ako je plan pomaknut.
                  </p>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Od datuma</label>
                      <input
                        type="date"
                        value={range.fromDate}
                        onChange={(e) => setRange({ ...range, fromDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Do datuma</label>
                      <input
                        type="date"
                        value={range.toDate}
                        onChange={(e) => setRange({ ...range, toDate: e.target.value })}
                      />
                    </div>
                  </div>

                  {batchesLoading ? (
                    <p>Učitavanje zamjesa…</p>
                  ) : batches.length === 0 ? (
                    <p className="empty-state">Nema zamjesa ovog proizvoda u odabranom rasponu.</p>
                  ) : (
                    <div className="mes-table-wrap">
                      <table className="mes-table">
                        <thead>
                          <tr>
                            <th>Zamjes (MixCode)</th>
                            <th>Datum</th>
                            <th style={{ textAlign: "right" }}>Masa (kg)</th>
                            <th>Mikser</th>
                            <th>Status</th>
                            <th>Procesni podaci</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {batches.map((b) => (
                            <React.Fragment key={b.ID}>
                              <tr>
                                <td>
                                  <strong>{b.MixCode}</strong>
                                </td>
                                <td>{formatHrDate(b.ProductionDate || b.PlanProductionDate)}</td>
                                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                                  {formatNum(b.FinalMassKg)}
                                </td>
                                <td>{b.MixerName || "-"}</td>
                                <td>
                                  <span className={`status-pill status-${b.BatchStatus}`}>
                                    {batchStatusLabel(b.BatchStatus)}
                                  </span>
                                </td>
                                <td>
                                  <span className={`status-badge ${b.ProcessParameter ? "ok" : "neutral"}`}>
                                    {b.ProcessParameter ? "Uneseno" : "Nije uneseno"}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="btn-edit btn-sm"
                                    onClick={() => setExpandedBatch(expandedBatch === b.ID ? null : b.ID)}
                                  >
                                    {expandedBatch === b.ID ? "Zatvori" : "Uredi"}
                                  </button>
                                </td>
                              </tr>
                              {expandedBatch === b.ID && (
                                <tr>
                                  <td colSpan={7}>
                                    <BatchProcessDataEditor
                                      batch={b}
                                      productionDate={String(
                                        b.ProductionDate || b.PlanProductionDate || range.toDate
                                      ).slice(0, 10)}
                                      parameter={b.ProcessParameter}
                                      onSaved={() => selected && loadBatches(selected.ID, range)}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>
                Zatvori
              </button>
              {(isNew || modalTab === "order") && (
                <button className="btn-submit" onClick={saveOrder} disabled={saving}>
                  {saving ? "Spremanje…" : isNew ? "Kreiraj nalog" : "Spremi nalog"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionWorkOrders;
